import { ElementType, ReactNode } from 'react';

import { styled, Box, BoxProps } from '@mui/material';
import clsx from 'clsx';

import { Sizes, SxProps } from '../../../types';
import { classNamesFor } from '../../../utils/classNames';
import { Info } from '../../icons';
import { TooltipProps } from '../../tooltip';
import { Tooltip } from '../../tooltip/Tooltip';

const classNamesFactory = classNamesFor('FormLabel');

// TODO (Justas): Export properly once we refactor this component to stop using mui theme, as now it would require a nasty typecast to attach to `styled` component
const classNames = {
  root: classNamesFactory.root(),
  disabled: classNamesFactory.globalClassNames.disabled,
};

export type FormLabelProps = Pick<BoxProps, 'component'> & {
  children?: ReactNode;
  labelTooltipProps?: Partial<TooltipProps>;
  size?: Sizes<'sm' | 'md' | 'lg'>;
  sx?: SxProps;
  icon?: ElementType;
  disabled?: boolean;
  className?: string;
  empty?: boolean;
  id?: string;
  htmlFor?: string;
  testId?: string;
};

const InfoIcon = styled(Info, {
  name: 'DsFormLabel',
  slot: 'Icon',
  target: 'DS-FormLabel-icon',
  overridesResolver: (_, styles) => {
    return styles.icon;
  },
})({});

const DsFormLabel = styled(
  ({
    children,
    id,
    htmlFor,
    labelTooltipProps = {},
    icon,
    className,
    disabled,
    empty,
    sx,
    testId,
    ...restProps
  }: FormLabelProps) => {
    if (!empty && !(children ?? labelTooltipProps.title)) {
      return null;
    }
    return (
      <Box
        component="label"
        id={id}
        htmlFor={htmlFor}
        className={clsx(
          classNames.root,
          className,
          disabled && classNames.disabled,
          disabled && 'DS-disabled'
        )}
        data-testid={testId}
        sx={sx}
        {...restProps}
      >
        {(children ?? empty) && (
          <span className="DS-InputLabel-label">
            {children ?? (empty && ' ')}
          </span>
        )}
        {labelTooltipProps.title && (
          <Tooltip
            placement="top"
            arrow
            {...labelTooltipProps}
            title={labelTooltipProps.title}
          >
            <InfoIcon as={icon} />
          </Tooltip>
        )}
      </Box>
    );
  },
  {
    name: 'DsFormLabel',
    slot: 'Root',
    target: 'DS-FormLabel-root',
    overridesResolver: ({ size = 'md', empty }, styles) => {
      return [styles.root, styles[size], empty && styles.empty];
    },
  }
)({});

export default DsFormLabel;
